if (!customElements.get('sensic-photo-gallery')) {
  customElements.define('sensic-photo-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.momentum);
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.track = this.querySelector('.sensic-ugc__track');
      this.dialog = this.querySelector('dialog');
      this.photos = [...this.querySelectorAll('[data-ugc-open]')];
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const progress = this.querySelector('[data-ugc-progress]');
      const prev = this.querySelector('[data-ugc-prev]');
      const next = this.querySelector('[data-ugc-next]');
      const update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        progress.style.width = `${Math.max(12, this.track.clientWidth / this.track.scrollWidth * 100)}%`;
        progress.style.marginLeft = `${max > 0 ? this.track.scrollLeft / max * (100 - parseFloat(progress.style.width)) : 0}%`;
        prev.disabled = this.track.scrollLeft <= 1;
        next.disabled = this.track.scrollLeft >= max - 1;
      };
      this.track.addEventListener('scroll', update, { ...opts, passive: true });
      this.resize = new ResizeObserver(update);
      this.resize.observe(this.track);
      const move = direction => {
        cancelAnimationFrame(this.momentum);
        this.track.scrollBy({ left: direction * this.track.clientWidth * .75, behavior: this.reduced ? 'instant' : 'smooth' });
      };
      prev.addEventListener('click', () => move(-1), opts);
      next.addEventListener('click', () => move(1), opts);
      this.track.addEventListener('dragstart', e => e.preventDefault(), opts);
      this.track.addEventListener('pointerdown', e => {
        cancelAnimationFrame(this.momentum);
        this.drag = null;
        this.activation = null;
        this.track.scrollTo({ left: this.track.scrollLeft, behavior: 'instant' });
        if (e.button > 0 || e.isPrimary === false) return;
        this.suppressClick = false;
        this.activation = { button: e.target.closest('[data-ugc-open]'), valid: false };
        this.drag = { id: e.pointerId, start: e.clientX, startY: e.clientY, scrollStart: this.track.scrollLeft, last: e.clientX, time: performance.now(), velocity: 0, moved: false };
      }, opts);
      this.track.addEventListener('pointermove', e => {
        const d = this.drag;
        if (!d || d.id !== e.pointerId) return;
        if (!d.moved && Math.abs(e.clientY - d.startY) > Math.abs(e.clientX - d.start) && Math.abs(e.clientY - d.startY) > 6) { this.drag = null; this.activation = null; this.suppressClick = true; return; }
        if (!d.moved && Math.abs(e.clientX - d.start) < 6) return;
        if (!d.moved) { d.moved = true; this.track.setPointerCapture(e.pointerId); this.track.classList.add('is-dragging'); }
        e.preventDefault();
        const now = performance.now(), dx = d.last - e.clientX;
        this.track.scrollLeft += dx;
        d.velocity = .65 * (dx / Math.max(8, now - d.time)) + .35 * d.velocity;
        d.last = e.clientX; d.time = now;
      }, opts);
      const release = e => {
        const d = this.drag;
        if (!d || d.id !== e.pointerId) return;
        this.drag = null;
        this.track.classList.remove('is-dragging');
        if (this.track.hasPointerCapture(e.pointerId)) this.track.releasePointerCapture(e.pointerId);
        const tap = e.type === 'pointerup' && !d.moved && Math.hypot(e.clientX - d.start, e.clientY - d.startY) < 6 && Math.abs(this.track.scrollLeft - d.scrollStart) < 3;
        if (this.activation) this.activation.valid = tap;
        this.suppressClick = !tap;
        if (!d.moved || this.reduced || e.type === 'pointercancel') return;
        let velocity = performance.now() - d.time > 90 ? 0 : d.velocity;
        let last = performance.now();
        const coast = now => {
          const dt = Math.min(32, now - last); last = now;
          const before = this.track.scrollLeft;
          this.track.scrollLeft += velocity * dt;
          velocity *= Math.exp(-dt / 180);
          if (Math.abs(velocity) > .02 && Math.abs(this.track.scrollLeft - before) > .1) this.momentum = requestAnimationFrame(coast);
        };
        this.momentum = requestAnimationFrame(coast);
      };
      this.track.addEventListener('pointerup', release, opts);
      this.track.addEventListener('pointercancel', release, opts);
      this.track.addEventListener('pointerleave', e => { if (this.drag && !this.drag.moved) { this.drag = null; this.activation = null; this.suppressClick = true; } }, opts);
      this.track.addEventListener('click', e => {
        const clicked = e.target.closest('[data-ugc-open]');
        if (!clicked) return;
        const activation = this.activation;
        this.activation = null;
        const pointerClick = !!activation || e.detail !== 0 || !!e.pointerType;
        if (pointerClick && (this.suppressClick || !activation?.valid)) { e.preventDefault(); return; }
        const button = pointerClick ? activation.button : clicked;
        if (!button || !this.track.contains(button)) return;
        e.preventDefault();
        cancelAnimationFrame(this.momentum);
        this.track.scrollTo({ left: this.track.scrollLeft, behavior: 'instant' });
        this.photos = [...this.track.querySelectorAll('[data-ugc-open]')];
        const index = this.photos.indexOf(button);
        if (index < 0) return;
        this.opener = button;
        this.show(index);
        if (!this.dialog.open) this.dialog.showModal();
      }, opts);
      this.querySelector('[data-ugc-close]').addEventListener('click', () => this.dialog.close(), opts);
      this.querySelector('[data-ugc-lightbox-prev]').addEventListener('click', () => this.show(this.index - 1), opts);
      this.querySelector('[data-ugc-lightbox-next]').addEventListener('click', () => this.show(this.index + 1), opts);
      this.dialog.addEventListener('click', e => { if (e.target === this.dialog) this.dialog.close(); }, opts);
      this.dialog.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); this.show(this.index + (e.key === 'ArrowLeft' ? -1 : 1)); }
      }, opts);
      this.dialog.addEventListener('close', () => { this.imageRequest = null; this.activation = null; this.suppressClick = false; this.opener?.focus({ preventScroll: true }); }, opts);
      update();
    }
    show(index) {
      this.photos = [...this.track.querySelectorAll('[data-ugc-open]')];
      if (!this.photos.length) return;
      this.index = (index + this.photos.length) % this.photos.length;
      const button = this.photos[this.index], thumb = button.querySelector('img');
      const previous = this.querySelector('[data-ugc-large]');
      const img = document.createElement('img');
      img.setAttribute('data-ugc-large', '');
      img.alt = thumb.alt;
      img.width = Number(button.dataset.ugcWidth) || thumb.naturalWidth || thumb.width || 1600;
      img.height = Number(button.dataset.ugcHeight) || thumb.naturalHeight || thumb.height || 1600;
      img.style.setProperty('--ugc-image-ratio', String(img.width / img.height));
      img.src = thumb.currentSrc || thumb.src || button.dataset.ugcOpen;
      previous.replaceWith(img);
      const request = {};
      this.imageRequest = request;
      const full = new Image();
      full.onload = () => {
        if (this.imageRequest !== request || !img.isConnected) return;
        img.src = full.src;

      };
      full.src = button.dataset.ugcOpen;
      this.querySelector('[data-ugc-counter]').textContent = `${this.index + 1} / ${this.photos.length}`;
      this.querySelector('[data-ugc-caption]').textContent = button.dataset.caption || '';
    }
    disconnectedCallback() {
      this.imageRequest = null;
      cancelAnimationFrame(this.momentum);
      this.abort?.abort(); this.resize?.disconnect();
      if (this.dialog?.open) this.dialog.close();
    }
  });
}
